import React from "react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Container,
  Card,
  CardContent,
  Stack,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import { motion } from "framer-motion";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import { useSite } from "hooks/use-site";
import { useNavigate } from "react-router-dom";
import { useDebounceEffect } from "ahooks";
import { confirmPhone, sendSmsCode } from "service/api";
import { errorNotification } from "features/notifications/notificationsSlice";
import { useAppDispatch } from "store";
import { useAccountQuery } from "features/auth/authApi";

type InitialValues = {
  code: string;
};

const validationSchema = Yup.object().shape({
  code: Yup.string().required("Code is required"),
});

const initialValues: InitialValues = {
  code: "",
};

const UserMobileVerification = () => {
  const { data: account } = useAccountQuery();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { logo: logoSrc, frontWalletAuthPageColor } = useSite();

  useDebounceEffect(
    () => {
      if (account?.user.idp) {
        sendSmsCode(account?.user.idp).then((res) => {
          if (!res.result) {
            navigate("/signin");
          }
        });
      } else {
        navigate("/signin");
      }
    },
    [],
    {
      wait: 0,
    }
  );

  const onSubmit = (
    values: InitialValues,
    { setSubmitting }: FormikHelpers<InitialValues>
  ) => {
    setSubmitting(true);

    if (account?.user.idp) {
      confirmPhone({ clientId: account?.user.idp, ...values })
        .then(() => {
          navigate("/");
        })
        .catch((err) => {
          dispatch(errorNotification(err?.message || "Wrong code"));
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const {
    handleSubmit,
    handleChange,
    getFieldMeta,
    handleBlur,
    isValid,
    isSubmitting,
  } = useFormik<InitialValues>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  return (
    <Box
      display={"flex"}
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box
              paddingY={2.5}
              paddingX={4}
              sx={{ bgcolor: `${frontWalletAuthPageColor}` }}
            >
              <img src={logoSrc} alt="logo" width={"200"} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight={"600"}>
                    Mobile Verification
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography variant="h6">
                    An SMS with verification code was sent to{" "}
                    {account?.user.phone}
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={0.5}>
                  <Grid xs={12} item>
                    <TextField
                      label="Received Code"
                      name="code"
                      variant="outlined"
                      value={getFieldMeta("code").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={2}
                    alignItems="center"
                  >
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                      disabled={!isValid || isSubmitting}
                    >
                      Submit
                    </LoadingButton>
                    <LoadingButton
                      onClick={() => {
                        if (account?.user.idp) {
                          sendSmsCode(account?.user.idp).then((res) => {
                            if (!res.result) {
                              navigate("/signin");
                            }
                          });
                        }
                      }}
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      color="info"
                    >
                      Resend the code
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default UserMobileVerification;
